/**
 * 重置用户密码弹窗（旧 UserDetail.vue 内的弹窗）：先生成 8 位随机密码，再确认重置；
 * 重置成功后保留 5 秒供管理员复制
 */
import { useEffect, useRef, useState } from 'react'
import { Alert, App, Descriptions, Modal, Typography } from 'antd'
import { generateRandomPassword } from './constants'
import { useResetUserPassword } from './hooks'

const CLOSE_DELAY_MS = 5000

interface ResetPasswordModalProps {
  open: boolean
  userId: number
  username: string
  onClose: () => void
}

export function ResetPasswordModal({ open, userId, username, onClose }: Readonly<ResetPasswordModalProps>) {
  const { message } = App.useApp()
  const [password, setPassword] = useState('')
  const resetPassword = useResetUserPassword()
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const close = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = null
    setPassword('')
    onClose()
  }

  const handleOk = async () => {
    if (!password) {
      setPassword(generateRandomPassword())
      return
    }
    try {
      await resetPassword.mutateAsync({ userId, password })
      message.success(`密码重置成功！新密码：${password}`)
      closeTimer.current = setTimeout(close, CLOSE_DELAY_MS)
    } catch (error) {
      console.error('重置密码失败:', error)
      message.error('重置密码失败')
    }
  }

  return (
    <Modal
      open={open}
      title="重置用户密码"
      okText={password ? '确认重置' : '生成密码'}
      okButtonProps={{ danger: true, loading: resetPassword.isPending }}
      onOk={handleOk}
      onCancel={close}
    >
      <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="用户名">{username}</Descriptions.Item>
        {password && (
          <Descriptions.Item label="新密码">
            <Typography.Text copyable={{ text: password }} code strong>
              {password}
            </Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>
      {password ? (
        <Alert
          title="⚠️ 重要提醒"
          type="warning"
          showIcon
          description={
            <div>
              <p>1. 请复制密码并告知用户</p>
              <p>2. 密码重置后，用户需要使用新密码重新登录</p>
              <p>3. 此操作不可撤销</p>
            </div>
          }
        />
      ) : (
        <Alert title="提示" description="点击【生成密码】按钮将自动生成8位随机密码" type="info" showIcon />
      )}
    </Modal>
  )
}
